import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';

import { API_BASE_URL } from '../../config';
const API = API_BASE_URL;

export default function AdminRegistrationSurvey() {
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState([]);
  const [qType, setQType] = useState('checkbox');
  const [qLabel, setQLabel] = useState('');
  const [qOptions, setQOptions] = useState('');
  const [qSaveToProfile, setQSaveToProfile] = useState(false);
  const [qProfileKey, setQProfileKey] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [selectedRegistrationId, setSelectedRegistrationId] = useState(null);
  const [insertAfter, setInsertAfter] = useState(-1);
  const [activeForNewUsers, setActiveForNewUsers] = useState(true);
  const dragState = React.useRef({ src: -1, over: -1 });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  function onDragStart(e, idx) {
    dragState.current.src = idx;
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch(e) {}
    e.dataTransfer.effectAllowed = 'move';
  }

  function onDragOver(e, idx) {
    e.preventDefault();
    dragState.current.over = idx;
  }

  function onDrop(e, idx) {
    e.preventDefault();
    const src = dragState.current.src;
    const dest = idx;
    if (src === -1 || dest === -1 || src === dest) return;
    setQuestions(prev => {
      const arr = [...prev];
      const [item] = arr.splice(src, 1);
      arr.splice(dest, 0, item);
      return arr;
    });
    dragState.current.src = -1;
    dragState.current.over = -1;
  }

  function onDragEnd() {
    dragState.current.src = -1;
    dragState.current.over = -1;
  }

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) setSelectedRegistrationId(Number(id));
    fetchAllTemplates(id ? Number(id) : null);
  }, []);

  async function fetchAllTemplates(selectedId = null) {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/surveys/templates`, { credentials: 'include' });
      const j = await r.json();
      const all = j.templates || [];
      setTemplates(all);
      // find registration templates by key heuristics
      const reg = all.filter(x => ((x.key||'').toLowerCase().includes('register') || x.key === 'registration'));
      if (selectedId) {
        const byId = all.find(x => x.id === selectedId);
        if (byId) populateTemplate(byId);
        else if (reg.length) populateTemplate(reg[0]);
        else populateTemplate(devFallback);
      } else if (reg.length) {
        populateTemplate(reg[0]);
      } else if (all.length) {
        // fallback to first template if none match registration heuristics
        populateTemplate(all[0]);
      } else {
        // nothing found — use dev fallback so admin sees something in UI
        setTemplates([devFallback]);
        populateTemplate(devFallback);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  // Fallback template for dev when backend is unreachable
  const devFallback = {
    id: 1,
    key: 'registration',
    title: 'Kayıt Anketi - Örnek (fallback)',
    version: 1,
    questions: [
      { id: 'pref', type: 'checkbox', label: 'Herhangi bir beslenme tercihiniz veya alerjiniz var mı?', options: ['Vegan', 'Vejetaryen', 'Glutensiz', 'Laktozsuz', 'Hiçbiri'] },
      { id: 'serving', type: 'number', label: 'Yemekleriniz genelde kaç kişilik?', options: [] },
      { id: 'frequency', type: 'dropdown', label: 'Haftada kaç gün evde yemek yapıyorsunuz?', options: ['1-2 Gün', '3-4 Gün', 'Her Gün'] }
    ],
    audience: 'all',
    activeForNewUsers: true
  };

  function populateTemplate(t) {
    setTemplate(t);
    setEditingId(t.id);
    setTitle(t.title || '');
    setQuestions(t.questions || []);
    setSelectedRegistrationId(t.id);
    setActiveForNewUsers(!!t.activeForNewUsers);
  }

  function addQuestion() {
    const opts = (qOptions || '').split(',').map(s => s.trim()).filter(Boolean);
    setQuestions(prev => {
      // Eğer saveToProfile seçiliyse ve profileKey boşsa, label'dan öner
      let profileKeyFinal = qSaveToProfile ? (qProfileKey || suggestProfileKeyFromLabel(qLabel)) : undefined;
      if (profileKeyFinal === '' || profileKeyFinal === 'none') profileKeyFinal = undefined;
      const newQ = { id: `q${prev.length+1}`, type: qType, label: qLabel, options: opts, saveToProfile: !!qSaveToProfile, ...(profileKeyFinal ? { profileKey: profileKeyFinal } : {}) };
      if (insertAfter === -1 || insertAfter >= prev.length - 1) return [...prev, newQ];
      // insert after index insertAfter
      const idx = insertAfter + 1;
      return [...prev.slice(0, idx), newQ, ...prev.slice(idx)];
    });
    setQLabel(''); setQOptions('');
    setQSaveToProfile(false);
    setQProfileKey('');
  }

  function suggestProfileKeyFromLabel(label) {
    if (!label) return '';
    const l = label.toLowerCase();
    if (l.includes('meslek') || l.includes('iş') || l.includes('pozisyon') || l.includes('unvan')) return 'jobTitle';
    if (l.includes('porsiyon') || l.includes('kişilik') || l.includes('kaç kişilik')) return 'servingSize';
    if (l.includes('telefon') || l.includes('tel')) return 'phone';
    if (l.includes('lokal') || l.includes('ülke') || l.includes('dil')) return 'locale';
    if (l.includes('yemek') && l.includes('sıklık')) return 'frequency';
    if (l.includes('tercih') || l.includes('diyet') || l.includes('alerj')) return 'preferences';
    // 'medeni' (medeni durum) removed from automatic profile-key suggestions
    return '';
  }

  async function saveTemplate() {
    if (!title || !questions.length) return alert('Başlık ve en az 1 soru gerekli');
    try {
      const body = { key: template?.key || 'registration', title, questions, audience: 'all', activeForNewUsers };
      let r, j;
      if (editingId) {
        r = await fetch(`${API}/api/surveys/templates/${editingId}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        j = await r.json();
        if (j.ok && j.template) populateTemplate(j.template);
      } else {
        r = await fetch(`${API}/api/surveys/templates`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
        j = await r.json();
        if (j.ok && j.template) populateTemplate(j.template);
      }
      if (!j.ok) return alert('Kaydetme hatası: ' + (j.error || ''));
      alert('Kaydedildi');
      // refresh list
      fetchAllTemplates();
    } catch (e) { console.error(e); alert('hata'); }
  }

  return (
    <div className="flex h-screen w-full">
      <AdminSidebar mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />
      <main className="flex-1 flex flex-col h-full overflow-hidden bg-background-light relative md:ml-[var(--admin-sidebar-width,18rem)] transition-[margin] duration-300">
        <AdminTopBar onToggleMobile={() => setMobileSidebarOpen(open => !open)} />

        <div className="flex-1 overflow-y-auto p-6 lg:p-10">
          <div className="bg-white p-4 rounded shadow max-w-3xl">
            <h2 className="text-xl font-bold mb-4">Kayıt Anketi — Yönet</h2>
        {loading ? <div>Yükleniyor...</div> : (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">Mevcut Kayıt Anketi</label>
              {templates.filter(x => ((x.key||'').toLowerCase().includes('register') || x.key === 'registration')).length ? (
                <select value={selectedRegistrationId || ''} onChange={e => {
                  const id = Number(e.target.value);
                  const t = templates.find(x => x.id === id);
                  if (t) populateTemplate(t);
                }} className="mt-1 block w-full rounded border-gray-300 p-2">
                  {templates.filter(x => ((x.key||'').toLowerCase().includes('register') || x.key === 'registration')).map(t => (
                    <option key={t.id} value={t.id}> {t.title} (v{t.version}) — {t.key}</option>
                  ))}
                </select>
              ) : <div className="text-sm text-gray-500">Kayıt anketi şablonu bulunamadı. Aşağıdan yeni bir şablon oluşturun.</div>}
            </div>

            <label className="block text-sm">Başlık</label>
            <input className="w-full mb-2" value={title} onChange={e=>setTitle(e.target.value)} />

            <label className="inline-flex items-center gap-2 mb-3">
              <input type="checkbox" checked={activeForNewUsers} onChange={e=>setActiveForNewUsers(e.target.checked)} className="h-4 w-4" />
              <span className="text-sm">Yeni kullanıcıya otomatik ata</span>
            </label>
            <div className="border p-2 rounded mb-2">
              <div className="flex gap-2 items-center mb-2">
                <select value={qType} onChange={e=>setQType(e.target.value)} className="p-1">
                  <option value="checkbox">Checkbox</option>
                  <option value="dropdown">Dropdown</option>
                </select>
                <input placeholder="Soru metni" value={qLabel} onChange={e=>setQLabel(e.target.value)} className="flex-1 p-1" />
              </div>
              <div className="mb-2">
                <label className="text-xs">Seçenekler (virgülle ayır)</label>
                <input value={qOptions} onChange={e=>setQOptions(e.target.value)} className="w-full p-1" />
              </div>
              <div className="flex gap-2 items-center mb-2">
                <label className="text-sm">Hangi sorudan sonra ekle?</label>
                <select value={insertAfter} onChange={e=>setInsertAfter(Number(e.target.value))} className="ml-2 p-1 border rounded">
                  <option value={-1}>Sondan sonra</option>
                  {questions.map((q,i) => <option key={i} value={i}>{i+1}. soru</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <label className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={qSaveToProfile} onChange={e=>setQSaveToProfile(e.target.checked)} className="h-4 w-4" />
                  <span className="text-sm">Kullanıcı profiline yaz</span>
                </label>
                <select
                  title="Profil anahtarı whitelist: sadece izin verilen alanlara yazılır"
                  value={qProfileKey === '' || qProfileKey === undefined ? 'custom' : ([
                    'servingSize','jobTitle','phone','avatar','locale','name','frequency','preferences','diet','cookingDays'
                  ].includes(qProfileKey) ? qProfileKey : 'custom')}
                  onChange={e => {
                    if (e.target.value === 'custom') setQProfileKey('');
                    else if (e.target.value === 'none') setQProfileKey('');
                    else setQProfileKey(e.target.value);
                  }}
                  className="p-1 border rounded"
                >
                  <option value="none">-- Profil anahtarı seç (opsiyonel) --</option>
                  <option value="servingSize">servingSize</option>
                  <option value="jobTitle">jobTitle</option>
                  <option value="phone">phone</option>
                  <option value="avatar">avatar</option>
                  <option value="locale">locale</option>
                  <option value="name">name</option>
                  <option value="frequency">frequency</option>
                  <option value="preferences">preferences</option>
                  <option value="diet">diet</option>
                  <option value="cookingDays">cookingDays</option>
                  <option value="custom">Özel...</option>
                </select>
                <button
                  type="button"
                  title="Etiketten öneri al"
                  onClick={() => {
                    const s = suggestProfileKeyFromLabel(qLabel);
                    setQProfileKey(s || '');
                  }}
                  className="px-2 py-1 border rounded text-sm"
                >Öner</button>
                {(qProfileKey === '' || (qProfileKey && !['servingSize','jobTitle','phone','avatar','locale','name','frequency','preferences','diet','cookingDays'].includes(qProfileKey))) && (
                  <input
                    placeholder="Özel anahtar girin"
                    value={qProfileKey}
                    onChange={e => setQProfileKey(e.target.value)}
                    className="p-1 border rounded flex-1"
                  />
                )}
              </div>
              <div className="text-xs text-gray-500 mb-2">İzin verilen anahtarlar: servingSize, jobTitle, phone, avatar, locale, name, frequency, preferences, diet, cookingDays</div>
              <button onClick={addQuestion} className="px-3 py-1 bg-primary text-white rounded">Soru Ekle</button>
            </div>

            <div className="mb-2">
              <h4 className="text-sm font-medium">Sorular</h4>
                        <div className="mt-2">
                          <h5 className="text-xs text-gray-500 mb-2">Sürükle bırak ile sırala</h5>
                          <ul className="ml-0 space-y-2">
                            {questions.map((q, i) => (
                              <li key={q.id || i} draggable onDragStart={(e)=>onDragStart(e,i)} onDragOver={(e)=>onDragOver(e,i)} onDrop={(e)=>onDrop(e,i)} onDragEnd={onDragEnd} className="flex items-center gap-3 p-2 border rounded bg-white">
                                <span className="cursor-grab text-gray-400">☰</span>
                                <div className="flex-1">
                                  <div className="font-medium">{q.label} {q.saveToProfile ? <span className="ml-2 text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">Profil: {q.profileKey || q.id}</span> : null}</div>
                                  <div className="text-xs text-gray-500">{q.type} — {Array.isArray(q.options)?q.options.join(', '):'—'}</div>
                                </div>
                                <div className="text-sm text-gray-500">{i+1}</div>
                              </li>
                            ))}
                          </ul>
                        </div>
            </div>

            <div className="flex gap-2 mb-4">
              <button onClick={saveTemplate} className="px-4 py-2 bg-primary text-white rounded">Kaydet</button>
              <button onClick={()=>{ setTitle(''); setQuestions([]); }} className="px-4 py-2 border rounded">Temizle</button>
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-2">Kayıt Anketi Bilgileri</h4>
              {template ? (
                <div className="text-sm text-gray-700">
                  <div><strong>Key:</strong> {template.key}</div>
                  <div><strong>Versiyon:</strong> v{template.version}</div>
                  <div className="mt-2"><strong>Sorular:</strong></div>
                  <ul className="list-disc ml-5">
                    {(template.questions || []).map((q, i) => <li key={i}>{q.label} ({q.type}) — {Array.isArray(q.options)?q.options.join(', '):''}</li>)}
                  </ul>
                </div>
              ) : <div className="text-sm text-gray-500">Kayıt anketi seçili değil.</div>}
            </div>
          </>
        )}
      </div>
    </div>
      </main>
    </div>
    );
  }
